import React, { useEffect, useState } from 'react';
import Toast, { ToastProps } from './Toast';

interface ToastContainerProps {
  toasts: ToastProps[];
  onClose: (id: string) => void;
}

interface AnimatedToast extends ToastProps {
  isVisible: boolean;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
  const [animatedToasts, setAnimatedToasts] = useState<AnimatedToast[]>([]);

  useEffect(() => {
    // Add new toasts with animation
    const newToasts = toasts.filter(
      toast => !animatedToasts.find(at => at.id === toast.id)
    );
    
    if (newToasts.length > 0) {
      const newAnimatedToasts = newToasts.map(toast => ({
        ...toast,
        isVisible: false
      }));
      
      setAnimatedToasts(prev => [...prev, ...newAnimatedToasts]);
      
      // Trigger animation after a brief delay
      setTimeout(() => {
        setAnimatedToasts(prev =>
          prev.map(toast =>
            newToasts.find(nt => nt.id === toast.id)
              ? { ...toast, isVisible: true }
              : toast
          )
        );
      }, 10);
    }

    // Remove toasts that are no longer in the list
    const removedToasts = animatedToasts.filter(
      at => !toasts.find(toast => toast.id === at.id)
    );
    
    if (removedToasts.length > 0) {
      // First, hide them with animation
      setAnimatedToasts(prev =>
        prev.map(toast =>
          removedToasts.find(rt => rt.id === toast.id)
            ? { ...toast, isVisible: false }
            : toast
        )
      );
      
      // Then remove them after animation completes
      setTimeout(() => {
        setAnimatedToasts(prev =>
          prev.filter(toast => !removedToasts.find(rt => rt.id === toast.id))
        );
      }, 300);
    }
  }, [toasts, animatedToasts]);

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-50"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {animatedToasts.map((toast) => (
          <div
            key={toast.id}
            className={`transform transition-all duration-300 ease-out ${
              toast.isVisible
                ? 'translate-y-0 opacity-100 sm:translate-x-0'
                : 'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2'
            }`}
          >
            <Toast {...toast} onClose={onClose} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ToastContainer;